import type { EnergyMember } from '../types/member';

// Relative hourly SLP weights [0..23] based on BDEW profiles adapted for Italy
// (see docs/profili_consumo_onboarding.md §3–6)
export const SLP_HOURLY: Record<string, number[]> = {
	p1_residential: [
		5, 4, 3, 3, 3, 4, 15, 20, 22, 18, 16, 18, 25, 22, 18, 16, 18, 20, 24, 35, 38, 32, 22, 12,
	],
	p2a_office: [
		10, 10, 10, 10, 10, 10, 10, 12, 30, 55, 65, 70, 60, 65, 70, 80, 100, 80, 40, 15, 10, 10, 10, 10,
	],
	p2b_retail: [
		5, 5, 5, 5, 5, 5, 5, 5, 5, 20, 50, 85, 90, 85, 60, 65, 80, 100, 100, 80, 20, 5, 5, 5,
	],
	p2c_food: [
		15, 15, 15, 15, 15, 15, 60, 100, 90, 70, 65, 70, 85, 90, 65, 50, 55, 65, 80, 90, 85, 75, 40, 20,
	],
	p3_industrial_2t: [
		60, 60, 60, 60, 60, 60, 70, 85, 95, 100, 98, 90, 80, 82, 88, 100, 98, 95, 85, 75, 68, 62, 60,
		60,
	],
	p4_industrial_h24: [
		85, 85, 85, 85, 85, 85, 88, 90, 92, 100, 100, 95, 90, 88, 90, 100, 100, 95, 90, 88, 87, 86, 85,
		85,
	],
};

// Monthly weights (Jan=0..Dec=11) — each array sums exactly to 12.0
// Derived from Terna/ISTAT load curves adapted for Italian context
export const SLP_MONTHLY = {
	residential: [1.12, 1.08, 1.0, 0.95, 0.92, 0.95, 1.05, 0.85, 0.92, 0.98, 1.06, 1.12],
	commercial: [1.05, 1.02, 1.0, 0.98, 0.97, 0.98, 0.97, 0.85, 0.97, 1.0, 1.02, 1.19],
	industrial: [1.02, 1.0, 1.0, 0.99, 0.99, 1.0, 0.99, 0.86, 0.99, 1.0, 1.04, 1.12],
} as const;

type MonthlyGroup = keyof typeof SLP_MONTHLY;

export function profileToGroup(profile: string | null | undefined): MonthlyGroup {
	if (!profile) return 'residential';
	if (profile.startsWith('p3') || profile.startsWith('p4')) return 'industrial';
	if (profile.startsWith('p2')) return 'commercial';
	return 'residential';
}

const RESIDENTIAL_TYPES = new Set(['residential']);
const COMMERCIAL_TYPES = new Set(['sme', 'office', 'shop', 'factory']);
const PA_TYPES = new Set(['public_administration', 'ets']);

export function getMemberEstimatedAnnualKwh(m: EnergyMember): number {
	const raw = m.consumptionProfileData?.consumoAnnuoKwh;
	if (!raw) return 0;
	const n = Number(raw);
	return Number.isFinite(n) && n > 0 ? n : 0;
}

export function getMemberMonthlyKwh(m: EnergyMember, monthIdx: number): number {
	const annual = getMemberEstimatedAnnualKwh(m);
	if (annual === 0) return 0;
	const group = profileToGroup(m.consumptionProfile);
	// SLP_MONTHLY sums to 12, so annual/12 * weight[month] = monthly kWh
	return Math.round((annual / 12) * SLP_MONTHLY[group][monthIdx]);
}

/** 24-element array of average hourly kW for a member given their annual kWh */
export function getMemberHourlyKw(m: EnergyMember): number[] {
	const annual = getMemberEstimatedAnnualKwh(m);
	if (annual === 0) return Array(24).fill(0);
	const profile = m.consumptionProfile ?? 'p1_residential';
	const weights = SLP_HOURLY[profile] ?? SLP_HOURLY.p1_residential;
	const totalWeight = weights.reduce((a, b) => a + b, 0);
	const avgKw = annual / 8760; // average kW over the year
	// Scale hourly weights to get actual kW per hour (24h sum = 24 * avgKw)
	const scale = (avgKw * 24) / totalWeight;
	return weights.map((w) => Math.round(w * scale * 10) / 10);
}

export interface EstimatedTotals {
	totalAnnualKwh: number;
	currentMonthKwh: number;
	byType: { residential: number; commercial: number; pa: number };
	perMember: Record<string, number>;
}

export function calcEstimatedTotals(members: EnergyMember[]): EstimatedTotals {
	const monthIdx = new Date().getMonth();
	let totalAnnual = 0;
	let resMonthly = 0;
	let comMonthly = 0;
	let paMonthly = 0;
	const perMember: Record<string, number> = {};

	for (const m of members) {
		if (m.stato === 'withdrawn') continue;
		const annual = getMemberEstimatedAnnualKwh(m);
		totalAnnual += annual;
		const monthly = getMemberMonthlyKwh(m, monthIdx);
		perMember[m.id] = monthly;
		if (RESIDENTIAL_TYPES.has(m.tipo)) resMonthly += monthly;
		else if (COMMERCIAL_TYPES.has(m.tipo)) comMonthly += monthly;
		else if (PA_TYPES.has(m.tipo)) paMonthly += monthly;
	}

	return {
		totalAnnualKwh: Math.round(totalAnnual),
		currentMonthKwh: resMonthly + comMonthly + paMonthly,
		byType: { residential: resMonthly, commercial: comMonthly, pa: paMonthly },
		perMember,
	};
}
